import { Component, Input } from '@angular/core';

/**
 * Section mark of the editorial sheet.
 *
 * These exist because the sheet used to borrow CATALOGUE field icons for editorial headings,
 * which is how "Carácter" ended up wearing the coffee cup that belongs to `foodPairings` and
 * how "Premios" and "Datos" ended up sharing one glyph. A section is not a catalogue field, so
 * it gets its own drawing.
 *
 * Same construction as the group icons: silhouette plus stroked detail on a 32-unit grid, drawn
 * for SpiritsRim, nothing traced from an icon library or a competition badge.
 */
export type EditorialSection =
    | 'character' | 'sensory' | 'tasting' | 'production'
    | 'ingredients' | 'affinities' | 'facts' | 'awards';

interface SectionGlyph {
  shape:  string;
  detail: string;
}

const SECTIONS: Record<string, SectionGlyph> = {
  // A drop over an open mouth: what the spirit tastes of.
  character: {
    shape: 'M16 4c3.6 3.4 5.4 6.6 5.4 9.6a5.4 5.4 0 0 1-10.8 0c0-3 1.8-6.2 5.4-9.6Z',
    detail: 'M6.5 21c2.6 3.5 5.7 5.3 9.5 5.3s6.9-1.8 9.5-5.3'
  },
  // A gauged vessel: the assessment, read as intensity rather than as a score.
  sensory: {
    shape: 'M16 3.5 26 9v14l-10 5.5L6 23V9Z',
    detail: 'M16 10.5v11M11 13v6M21 13v6'
  },
  // A copita: the tasting itself.
  tasting: {
    shape: 'M8 5h16l-1.4 7.6c-.7 3.6-3.2 5.6-6.6 5.6s-5.9-2-6.6-5.6Z',
    detail: 'M16 18.2V27M10.5 27h11'
  },
  // A pot still.
  production: {
    shape: 'M11 4h10l-1 8.4 5.4 10.2c1.2 2.3-.1 4.4-2.7 4.4H9.3c-2.6 0-3.9-2.1-2.7-4.4L12 12.4Z',
    detail: 'M9.5 21h13'
  },
  // A crate of raw materials.
  ingredients: {
    shape: 'M6 8h20v18H6Z',
    detail: 'M6 14h20M13 8v18'
  },
  // Fork and knife, kept abstract: it names a food family, not a dish on this menu.
  affinities: {
    shape: '',
    detail: 'M9 4v10c0 2 1 3 3 3.4V28M9 4v7M13.5 4v7M23 4c1.8 2.6 2.6 5.6 2.6 9 0 2.4-.9 4-2.6 4.6V28'
  },
  // A shield with a check: what we can state about the product.
  facts: {
    shape: 'M16 3.5l10 4v8.7c0 6-4 10.6-10 12.3-6-1.7-10-6.3-10-12.3V7.5Z',
    detail: 'M11.5 15.5l3.2 3.4 6-6.6'
  },
  // Our own medal. Never a competition's artwork.
  awards: {
    shape: 'M16 12.8a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2Z',
    detail: 'M11.4 10.6 8.4 4h15.2l-3 6.6'
  }
};

const FALLBACK: SectionGlyph = { shape: 'M16 5 27 16 16 27 5 16Z', detail: '' };

@Component({
  selector: 'app-editorial-icon',
  standalone: true,
  template: `
    <svg width="22" height="22" viewBox="0 0 32 32" fill="none" aria-hidden="true"
         focusable="false" xmlns="http://www.w3.org/2000/svg">
      @if (glyph.shape) {
        <path [attr.d]="glyph.shape" fill="currentColor" fill-opacity=".22"/>
        <path [attr.d]="glyph.shape" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"/>
      }
      @if (glyph.detail) {
        <path [attr.d]="glyph.detail" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"/>
      }
    </svg>
  `,
  styles: [':host { display: inline-flex; line-height: 0; }']
})
export class EditorialIconComponent {

  @Input() set section(value: EditorialSection | string | null | undefined) {
    this.glyph = (value && SECTIONS[value]) || FALLBACK;
  }

  glyph: SectionGlyph = FALLBACK;
}
